import { ReactNode, useRef } from 'react';

import { GoogleTagManager } from '@iwoca/frontend-tracking-library';
import classNames from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { FormattedDate, FormattedMessage } from 'react-intl';

import styles from './SelectAmountWithSlider.module.css';
import {
    denormalizeInputValue,
    denormlizeSliderValue,
    denormlizeSliderValueAmount,
    normalizeInputValue,
    normalizeSliderValue,
    serialiseAndRoundInputValue
} from './SliderHelper';
import { getCurrenySymbolFromCode } from '../../../helpers/currency';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { TooltipQuestionMarkSVG } from '../../../svgComponents/tooltipQuestionMarkSVG/index';
import { MoneyNoDecimals } from '../moneyNoDecimals/';
import { Tooltip } from '../tooltip/index';

type Props = {
    name: string;
    selectedAmount: number | null;
    selectedSliderAmount?: number | null;
    selectedInputAmount?: number | null;
    minValue: number;
    maxValue: number;
    locale?: string;
    currency?: string;
    isHPB?: boolean;
    approvalStatusTimeOut?: string;
    initialValues?: { amount: string };
    children?: ReactNode;
    updateSelectedAmount: (inputAmount: number, sliderAmount: number) => void;
    requestSelectedAmount: () => void;
};

type FormValues = { amount: number };

const SelectAmountWithSlider = ({
    name,
    children,
    minValue,
    maxValue,
    currency,
    locale,
    isHPB,
    approvalStatusTimeOut,
    selectedAmount,
    selectedSliderAmount,
    initialValues,
    updateSelectedAmount,
    requestSelectedAmount
}: Props) => {
    const sliderRef = useRef<HTMLInputElement>(null);

    const initialAmount = initialValues?.amount
        ? Number(denormalizeInputValue(initialValues.amount))
        : 0;

    const { setValue, getValues, control } = useForm<FormValues>({
        defaultValues: { amount: initialAmount }
    });

    const trackAndRequestSelectedAmount = () => {
        GoogleTagManager.trackSlider({
            sliderName: name,
            sliderValue: selectedAmount ?? 0
        });
        requestSelectedAmount();
    };

    const handleAmountInputSubmit = (
        event: React.FormEvent | React.FocusEvent
    ) => {
        event.preventDefault();
        const inputValue = getValues('amount');

        const serialisedInputValue = serialiseAndRoundInputValue(
            inputValue,
            maxValue,
            minValue
        );

        if (serialisedInputValue <= minValue) {
            setValue('amount', minValue);
        }

        updateSelectedAmount(serialisedInputValue, serialisedInputValue);
        trackAndRequestSelectedAmount();
    };

    const updateAndParse = (value: string) => {
        const formattedVal = denormalizeInputValue(value);
        updateSelectedAmount(formattedVal, formattedVal);
    };

    const handleFormatInput = (value: string | number | undefined) => {
        if (value === undefined || value === null || value === '') {
            return '';
        }
        return normalizeInputValue(value, maxValue, minValue, locale);
    };

    const handleParseInput = (value: string) => denormalizeInputValue(value);

    const handleSliderChange = () => {
        if (!sliderRef.current) return;
        const sliderValue = Number(sliderRef.current.value);

        const sliderAmountDenormalised = denormlizeSliderValue(
            sliderValue,
            maxValue,
            minValue
        );
        const inputValue = denormlizeSliderValueAmount(
            sliderAmountDenormalised,
            maxValue,
            minValue
        );

        updateSelectedAmount(inputValue, sliderAmountDenormalised);
        setValue('amount', inputValue);
    };

    const sliderValue = normalizeSliderValue(
        selectedSliderAmount ?? minValue,
        maxValue,
        minValue
    );

    const handleSliderOnAfterChange = () => trackAndRequestSelectedAmount();

    const currencySymbol = getCurrenySymbolFromCode(currency);
    const country = getCountry();
    const currencySymbolStyles = classNames(
        styles.currencySymbol,
        styles[`currencySymbol_${country}`],
        { [styles.currencySymbolHPB]: isHPB }
    );

    const renderTooltip = () => (
        <Tooltip
            message={
                <FormattedMessage
                    id="_.select_amount_with_slider.tooltip"
                    defaultMessage="If you don't take the full {amount} today, you've got until {date} to come back for the rest."
                    values={{
                        amount: <MoneyNoDecimals value={maxValue} />,
                        date: (
                            <FormattedDate
                                value={
                                    new Date(approvalStatusTimeOut as string)
                                }
                                day="2-digit"
                                month="long"
                                year="numeric"
                            />
                        )
                    }}
                />
            }
        >
            <TooltipQuestionMarkSVG
                className={classNames(styles.icon, styles.questionIcon)}
            />
        </Tooltip>
    );

    return (
        <div
            className={classNames(styles.SelectAmountWithSlider, {
                [styles.SelectAmountWithSliderHPB]: isHPB
            })}
            data-testid="choose-amount-slider-title"
        >
            <div
                className={classNames(styles.sliderTitle, {
                    [styles.sliderTitleHPB]: isHPB
                })}
            >
                <h3
                    className={classNames(styles.title, {
                        [styles.titleHPB]: isHPB
                    })}
                >
                    {children}
                </h3>
                <div
                    className={classNames(styles.formWrapper, {
                        [styles.formWrapperHPB]: isHPB
                    })}
                >
                    <form
                        className={classNames(styles.form, {
                            [styles.formHPB]: isHPB
                        })}
                        onSubmit={handleAmountInputSubmit}
                    >
                        <span
                            data-testid="choose-amount-input-currency"
                            className={currencySymbolStyles}
                        >
                            {currencySymbol}
                        </span>
                        <Controller
                            control={control}
                            name="amount"
                            render={({ field }) => (
                                <input
                                    ref={field.ref}
                                    name={field.name}
                                    type="text"
                                    inputMode="numeric"
                                    data-testid="choose-amount-input"
                                    className={classNames(styles.amountInput, {
                                        [styles.amountInputHPB]: isHPB
                                    })}
                                    value={handleFormatInput(field.value)}
                                    onChange={(e) => {
                                        const parsed = handleParseInput(
                                            e.target.value
                                        );
                                        field.onChange(parsed);
                                        updateAndParse(e.target.value);
                                    }}
                                    onBlur={(e) => {
                                        field.onBlur();
                                        handleAmountInputSubmit(e);
                                    }}
                                />
                            )}
                        />
                    </form>
                </div>
                {isHPB && renderTooltip()}
            </div>
            <div className={styles.sliderContainer}>
                <input
                    type="range"
                    ref={sliderRef}
                    className={styles.slider}
                    style={
                        {
                            '--proportion': `${(sliderValue / 1000) * 100}%`
                        } as React.CSSProperties
                    }
                    value={sliderValue}
                    onChange={handleSliderChange}
                    onKeyUp={handleSliderOnAfterChange}
                    onMouseUp={handleSliderOnAfterChange}
                    onBlur={handleSliderOnAfterChange}
                    onTouchEnd={handleSliderOnAfterChange}
                    min={0}
                    max={1000}
                />
            </div>
            <div className={styles.sliderMinMaxValues}>
                <div
                    data-testid="minimum-drawdown-amount"
                    className={styles.min}
                >
                    <MoneyNoDecimals value={minValue} />
                </div>
                <div
                    data-testid="maximum-drawdown-amount"
                    className={styles.max}
                >
                    <MoneyNoDecimals value={maxValue} />
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default SelectAmountWithSlider;
